import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LogOut, User, Bell, Menu, X, ChevronDown } from "lucide-react";
import PJPCoin from "@/components/PJPCoin";

function Monogram() {
  return (
    <div className="flex items-center gap-3">
      {/* Live 3D PJP coin logo — spins slowly, gracefully falls back to a static badge if WebGL is unavailable. */}
      <div
        className="h-11 w-11 shrink-0"
        style={{ filter: "drop-shadow(0 0 12px rgba(75, 156, 255, 0.35))" }}
        aria-label="PJP · Private Jet Pilots"
      >
        <PJPCoin speed={0.28} tilt={4} forceMotion />
      </div>
      <div className="leading-none hidden sm:block">
        <div className="font-display font-semibold text-lg tracking-tight text-white">Private Jet Pilots</div>
        <div className="mono text-[9px] text-sky/70 tracking-[0.24em] uppercase mt-1">Aviation Ops Network</div>
      </div>
    </div>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobile, setMobile] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  // Poll combined unread count (notifications + messages) every 30s
  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const [n, m] = await Promise.all([
          api.get("/notifications/unread-count"),
          api.get("/messages/unread-count"),
        ]);
        if (!cancelled) setUnread((n.data.unread_count || 0) + (m.data.unread_count || 0));
      } catch { /* ignore */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [user?.id]);

  // When embedded (e.g. Digital ID iframe on profile page), hide the site header entirely.
  if (typeof window !== "undefined" && window.location.search.includes("embed=1")) {
    return null;
  }

  const linkCls = ({ isActive }) =>
    `text-[13px] font-medium tracking-tight transition-colors ${isActive ? "text-sky" : "text-white/70 hover:text-white"}`;

  const publicNav = [
    { to: "/search", label: "Find Crew", tid: "nav-search" },
    { to: "/operators", label: "Operators", tid: "nav-operators" },
    { to: "/quote", label: "Instant PJ Quote", tid: "nav-quote", highlight: true },
    { to: "/empty-leg", label: "JumpSeat", tid: "nav-empty-leg" },
    { to: "/directory", label: "Directory", tid: "nav-directory" },
    { to: "/aerointel", label: "AeroIntel", tid: "nav-aerointel" },
    { to: "/jobs", label: "Job Board", tid: "nav-jobs" },
    { to: "/membership", label: "Membership", tid: "nav-membership" },
  ];

  const memberNav = [
    { to: "/dashboard", label: "Cockpit", tid: "nav-cockpit" },
    { to: "/feed", label: "Hot Mic", tid: "nav-feed", highlight: true },
    { to: "/unicom", label: "UNICOM", tid: "nav-unicom", highlight: true },
    { to: "/fuel", label: "PJP FUEL", tid: "nav-fuel", highlight: true },
    { to: "/search",    label: "Find Crew", tid: "nav-search" },
    { to: "/operators", label: "Operators", tid: "nav-operators" },
    { to: "/quote", label: "Instant PJ Quote", tid: "nav-quote", highlight: true },
    { to: "/empty-leg", label: "JumpSeat", tid: "nav-empty-leg" },
    // Only JumpSeat riders / paid crew get the dedicated Rider Cockpit link.
    ...((user?.membership_tier_slug === "empty_leg_rider" || ["pilot","flight_attendant","amt"].includes(user?.membership_tier_slug) || user?.is_admin)
      ? [{ to: "/rider/dashboard", label: "Rider Cockpit", tid: "nav-rider-dash", highlight: true }]
      : []),
    { to: "/wallet", label: "Wallet", tid: "nav-wallet" },
    // Grouped under "More" on desktop
    { to: "/aircraft-sales", label: "Aircraft Sales", tid: "nav-aircraft-sales", secondary: true },
    { to: "/logbook",   label: "Logbook",   tid: "nav-logbook", secondary: true },
    { to: "/phf",       label: "PHF",       tid: "nav-phf", secondary: true },
    { to: "/documents", label: "Documents", tid: "nav-documents", secondary: true },
    { to: "/aerointel", label: "AeroIntel", tid: "nav-aerointel", secondary: true },
    { to: "/jobs", label: "Job Board", tid: "nav-jobs", secondary: true },
    { to: "/aog", label: "AOG", tid: "nav-aog", secondary: true },
    { to: "/directory", label: "Directory", tid: "nav-directory", secondary: true },
    ...(user?.is_admin ? [{ to: "/admin", label: "Admin", tid: "nav-admin", secondary: true }] : []),
  ];

  const items = user ? memberNav : publicNav;

  // Emoji map — one glyph per nav destination, chosen to match the page's
  // intent. Rendered inside the mobile drawer + desktop "More" dropdown so
  // members can scan the list at a glance instead of reading each label.
  const NAV_EMOJI = {
    "nav-cockpit":        "🛩️",
    "nav-feed":           "🎙️",
    "nav-unicom":         "📡",
    "nav-fuel":           "⛽",
    "nav-search":         "👨‍✈️👩‍✈️",
    "nav-operators":      "🛫",
    "nav-quote":          "💰",
    "nav-empty-leg":      "🪂",
    "nav-rider-dash":     "🥇",
    "nav-wallet":         "💳",
    "nav-aircraft-sales": "🏷️",
    "nav-logbook":        "📓",
    "nav-phf":            "🛡️",
    "nav-documents":      "📄",
    "nav-aerointel":      "🤖",
    "nav-jobs":           "💼",
    "nav-aog":            "🚨",
    "nav-directory":      "📇",
    "nav-admin":          "⚙️",
    "nav-membership":     "🎫",
    "nav-inbox":          "📬",
    "nav-hire-now":       "🚁",
  };

  // Per-page color accents — each nav button gets its own tinted glass so
  // members recognise destinations by color (Directory = teal, AOG = red,
  // etc). Values are pre-computed RGB triples so we can build the exact
  // rgba() gradient / border / glow for each tile in one style call.
  const NAV_COLORS = {
    "nav-search":         "75,156,255",   // cyan · people
    "nav-operators":      "46,213,115",   // green · ops
    "nav-empty-leg":      "255,171,64",   // amber · empty-leg
    "nav-wallet":         "255,209,102",  // gold · wallet
    "nav-aircraft-sales": "191,124,255",  // purple · sales
    "nav-logbook":        "126,200,255",  // sky · logbook
    "nav-phf":            "94,215,255",   // ice · phf/insurance
    "nav-documents":      "159,168,255",  // periwinkle · docs
    "nav-aerointel":      "236,72,153",   // pink · AI
    "nav-jobs":           "251,146,60",   // orange · jobs
    "nav-aog":            "239,68,68",    // red · emergency
    "nav-directory":      "45,212,191",   // teal · directory
    "nav-admin":          "212,175,55",   // champagne · admin
    "nav-membership":     "212,175,55",   // champagne · membership
    "nav-hire-now":       "255,138,101",  // coral · hire
    "nav-inbox":          "75,156,255",   // cyan · inbox
  };

  return (
    <header className="sticky top-0 z-40 bg-midnight/85 backdrop-blur-xl border-b border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 py-5 flex items-center gap-6">
        <Link to="/" data-testid="brand-link" className="flex-shrink-0">
          <Monogram />
        </Link>

        <nav className="hidden lg:flex items-center gap-6 ml-6">
          {items.filter((it) => !it.secondary).map((it) => {
            // Cockpit gets its own signature green glass pill so members can
            // spot the "back to my hub" anchor at a glance.
            if (it.tid === "nav-cockpit") {
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={it.tid}
                  end
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full mono text-[10px] font-bold tracking-[0.22em] transition-all hover:scale-[1.04] ${
                      isActive ? "text-white" : "text-avail hover:text-white"
                    }`
                  }
                  style={{
                    background: "linear-gradient(90deg, rgba(46,213,115,0.22), rgba(46,213,115,0.08))",
                    border: "1px solid rgba(46,213,115,0.65)",
                    backdropFilter: "blur(14px)",
                    WebkitBackdropFilter: "blur(14px)",
                    boxShadow: "0 0 18px rgba(46,213,115,0.45), inset 0 0 14px rgba(46,213,115,0.10)",
                  }}
                >
                  <span className="relative inline-flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-avail opacity-70 animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-avail shadow-[0_0_6px_rgba(46,213,115,0.9)]" />
                  </span>
                  {it.label.toUpperCase()}
                </NavLink>
              );
            }
            return it.highlight ? (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.tid}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mono text-[10px] font-bold tracking-widest transition-all hover:scale-[1.04] ${
                  isActive ? "text-white" : "text-sky hover:text-white"
                }`
              }
              style={{
                background: "linear-gradient(90deg, rgba(75,156,255,0.18), rgba(126,200,255,0.10))",
                border: "1px solid rgba(75,156,255,0.55)",
                boxShadow: "0 0 14px rgba(75,156,255,0.35), inset 0 0 12px rgba(75,156,255,0.08)",
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-sky animate-pulse" />
              {it.label.toUpperCase()}
            </NavLink>
          ) : (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.tid}
              end={it.to === "/dashboard"}
              className={linkCls}
            >
              {it.label}
            </NavLink>
          );
          })}
          {items.some((it) => it.secondary) && (
            <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
              <button
                data-testid="nav-more"
                className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors py-1"
              >
                More <ChevronDown className="h-3.5 w-3.5" />
              </button>
              {moreOpen && (
                <div className="absolute right-0 top-full pt-2 z-40">
                  <div
                    className="rounded-2xl p-2 min-w-[240px] border border-white/15 overflow-hidden"
                    style={{
                      background: "linear-gradient(155deg, rgba(75,156,255,0.10) 0%, rgba(5,8,16,0.92) 60%)",
                      backdropFilter: "blur(22px) saturate(150%)",
                      WebkitBackdropFilter: "blur(22px) saturate(150%)",
                      boxShadow: "0 1px 0 rgba(255,255,255,0.06) inset, 0 30px 60px -20px rgba(0,0,0,0.7), 0 0 60px -30px rgba(75,156,255,0.55)",
                    }}
                  >
                    <div className="space-y-1">
                      {items.filter((it) => it.secondary).map((it) => {
                        const rgb = NAV_COLORS[it.tid] || "255,255,255";
                        return (
                          <NavLink
                            key={it.to}
                            to={it.to}
                            data-testid={it.tid}
                            onClick={() => setMoreOpen(false)}
                            className="relative flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all overflow-hidden"
                            style={{
                              background: `linear-gradient(135deg, rgba(${rgb},0.18) 0%, rgba(${rgb},0.05) 50%, rgba(5,8,16,0.55) 100%)`,
                              border: `1px solid rgba(${rgb},0.45)`,
                              boxShadow: `0 0 18px -8px rgba(${rgb},0.55)`,
                            }}
                          >
                            <span
                              aria-hidden="true"
                              className="absolute top-0 left-3 right-3 h-px pointer-events-none"
                              style={{ background: `linear-gradient(90deg, transparent, rgba(${rgb},0.85), transparent)` }}
                            />
                            <span className="text-lg leading-none relative">{NAV_EMOJI[it.tid] || "•"}</span>
                            <span className="text-sm font-medium text-white relative">{it.label}</span>
                          </NavLink>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/hire-now"
            data-testid="nav-hire-now"
            className="hidden md:inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full mono text-[11px] font-bold tracking-wider transition-all hover:scale-[1.03]"
            style={{ background: "#EB5757", color: "#fff", boxShadow: "0 0 14px rgba(235,87,87,0.35)" }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />
            HIRE NOW
          </Link>
          {user ? (
            <>
              <Link to="/inbox" data-testid="nav-inbox" className="relative btn btn-sm hidden md:inline-flex" title="Inbox">
                <Bell className="h-3.5 w-3.5" />
                <span className="hidden xl:inline">Inbox</span>
                {unread > 0 && (
                  <span
                    data-testid="nav-inbox-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full mono text-[10px] font-bold grid place-items-center"
                    style={{ background: "#4B9CFF", color: "#0A0F1A", boxShadow: "0 0 10px rgba(75,156,255,0.6)" }}
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </Link>
              <Link to="/dashboard#profile" data-testid="nav-profile" className="btn btn-sm">
                <User className="h-3.5 w-3.5" />
                <span className="hidden md:inline">{user.first_name}</span>
                {user.faa_verified && <span className="ml-1 h-1.5 w-1.5 rounded-full bg-avail animate-pulse" />}
              </Link>
              <button onClick={() => { logout(); nav("/"); }} data-testid="btn-logout" className="btn btn-sm hidden md:inline-flex" title="Logout">
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="btn btn-sm hidden md:inline-flex">Sign in</Link>
              <Link to="/membership" data-testid="nav-register" className="btn btn-sm btn-gold">Join PJP</Link>
            </>
          )}
          <button onClick={() => setMobile(!mobile)} data-testid="btn-mobile-menu" className="lg:hidden text-ivory p-2 ml-1">
            {mobile ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {mobile && (
        <div
          className="lg:hidden border-t border-white/[0.06] px-5 py-4 space-y-2.5"
          data-testid="mobile-drawer"
          style={{
            background: "linear-gradient(180deg, rgba(5,8,16,0.92) 0%, rgba(10,15,26,0.85) 100%)",
            backdropFilter: "blur(24px) saturate(150%)",
            WebkitBackdropFilter: "blur(24px) saturate(150%)",
          }}
        >
          {(() => {
            // Split items so Cockpit + highlighted CTAs stay full-width and
            // punchy, while the long tail of standard pages packs into a
            // 2-col grid — cuts drawer height roughly in half so members
            // don't have to scroll to reach Directory / Admin / etc.
            const featured = items.filter((it) => it.tid === "nav-cockpit" || it.highlight);
            const standard = items.filter((it) => it.tid !== "nav-cockpit" && !it.highlight);
            return (
              <>
                {featured.map((it) => {
                  if (it.tid === "nav-cockpit") {
                    return (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        end
                        onClick={() => setMobile(false)}
                        data-testid={`m-${it.tid}`}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-3 rounded-xl mono text-[12px] font-bold tracking-[0.22em] transition-all ${
                            isActive ? "text-white" : "text-avail"
                          }`
                        }
                        style={{
                          background: "linear-gradient(90deg, rgba(46,213,115,0.22), rgba(46,213,115,0.08))",
                          border: "1px solid rgba(46,213,115,0.65)",
                          backdropFilter: "blur(14px)",
                          WebkitBackdropFilter: "blur(14px)",
                          boxShadow: "0 0 18px rgba(46,213,115,0.45), inset 0 0 14px rgba(46,213,115,0.10)",
                        }}
                      >
                        <span className="text-xl leading-none">{NAV_EMOJI[it.tid]}</span>
                        <span className="flex-1">{it.label.toUpperCase()}</span>
                        <span className="relative inline-flex h-2 w-2">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-avail opacity-70 animate-ping" />
                          <span className="relative inline-flex h-2 w-2 rounded-full bg-avail" />
                        </span>
                      </NavLink>
                    );
                  }
                  return (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      onClick={() => setMobile(false)}
                      data-testid={`m-${it.tid}`}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all"
                      style={{
                        background: "linear-gradient(90deg, rgba(75,156,255,0.20), rgba(126,200,255,0.08))",
                        border: "1px solid rgba(75,156,255,0.55)",
                        backdropFilter: "blur(14px)",
                        WebkitBackdropFilter: "blur(14px)",
                        boxShadow: "0 0 14px rgba(75,156,255,0.30), inset 0 0 10px rgba(75,156,255,0.06)",
                      }}
                    >
                      <span className="text-xl leading-none">{NAV_EMOJI[it.tid] || "•"}</span>
                      <span className="mono text-[12px] font-bold tracking-widest text-sky uppercase flex-1">{it.label}</span>
                      <span className="h-1.5 w-1.5 rounded-full bg-sky animate-pulse" />
                    </NavLink>
                  );
                })}
                {/* Standard pages — 2 per row so the drawer fits in one screen */}
                <div className="grid grid-cols-2 gap-2 pt-1">
                  {standard.map((it) => {
                    const rgb = NAV_COLORS[it.tid] || "255,255,255";
                    return (
                      <NavLink
                        key={it.to}
                        to={it.to}
                        onClick={() => setMobile(false)}
                        data-testid={`m-${it.tid}`}
                        className="relative flex items-center gap-2 px-3 py-2.5 rounded-xl transition-all min-w-0 overflow-hidden group"
                        style={{
                          background: `linear-gradient(135deg, rgba(${rgb},0.18) 0%, rgba(${rgb},0.06) 50%, rgba(5,8,16,0.55) 100%)`,
                          border: `1px solid rgba(${rgb},0.45)`,
                          backdropFilter: "blur(18px) saturate(150%)",
                          WebkitBackdropFilter: "blur(18px) saturate(150%)",
                          boxShadow: `0 1px 0 rgba(255,255,255,0.05) inset, 0 8px 20px -10px rgba(0,0,0,0.5), 0 0 20px -8px rgba(${rgb},0.55)`,
                          color: `rgb(${rgb})`,
                        }}
                      >
                        {/* Top-edge light-catch */}
                        <span
                          aria-hidden="true"
                          className="absolute top-0 left-3 right-3 h-px pointer-events-none"
                          style={{ background: `linear-gradient(90deg, transparent, rgba(${rgb},0.9), transparent)` }}
                        />
                        <span className="text-lg leading-none flex-shrink-0 relative">{NAV_EMOJI[it.tid] || "•"}</span>
                        <span className="text-[13px] font-medium truncate text-white relative">{it.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {/* Auth footer inside the drawer */}
          {user ? (
            <div className="pt-3 mt-2 border-t border-white/[0.06] grid grid-cols-2 gap-2">
              <NavLink
                to="/inbox"
                onClick={() => setMobile(false)}
                data-testid="m-nav-inbox"
                className="relative flex items-center gap-2 px-3 py-2.5 rounded-xl border border-white/[0.08] bg-white/[0.03] hover:border-cyan/40 text-white/85"
              >
                <span className="text-lg leading-none">📬</span>
                <span className="text-[13px] font-medium flex-1">Inbox</span>
                {unread > 0 && (
                  <span
                    className="min-w-[20px] h-5 px-1.5 rounded-full mono text-[10px] font-bold grid place-items-center"
                    style={{ background: "#4B9CFF", color: "#0A0F1A" }}
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </NavLink>
              <button
                onClick={() => { logout(); setMobile(false); nav("/"); }}
                data-testid="m-btn-logout"
                className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-red-500/25 bg-red-500/[0.06] hover:bg-red-500/[0.14] text-red-300"
              >
                <span className="text-lg leading-none">🚪</span>
                <span className="text-[13px] font-medium flex-1 text-left">Sign out</span>
              </button>
            </div>
          ) : (
            <div className="pt-3 mt-2 border-t border-white/[0.06] grid grid-cols-2 gap-2">
              <NavLink to="/login" onClick={() => setMobile(false)} data-testid="m-nav-login" className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-white/[0.10] bg-white/[0.03] text-white/85">
                <span className="text-lg leading-none">🔑</span>
                <span className="text-[13px] font-medium flex-1">Sign in</span>
              </NavLink>
              <NavLink to="/membership" onClick={() => setMobile(false)} data-testid="m-nav-register" className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-champagne/40 bg-champagne/[0.10] text-champagne">
                <span className="text-lg leading-none">🎫</span>
                <span className="mono text-[11px] font-bold tracking-widest flex-1">JOIN PJP</span>
              </NavLink>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
